import { useEffect, useRef } from 'react';
import { usePageVisibility } from './usePageVisibility';

/**
 * Coming back to a tab costs one refresh, never three. Without a floor, an auditor
 * who flips between tabs three times a minute would pay MORE than one who sits and
 * watches -- the opposite of what this whole change promises.
 */
export const REFRESH_FLOOR_MS = 10000;

interface VisiblePollingOptions {
  /** Extra condition ANDed with visibility. Defaults to `true`. */
  enabled?: boolean;
}

/**
 * Polls `callback` every `intervalMs`, but only while the tab is on screen and
 * `enabled` holds. A hidden tab runs no interval at all: nothing to throttle, nothing
 * to catch up on. Coming back fires a single immediate refresh and restarts the
 * interval from that instant.
 *
 * The first mount does NOT fire: every caller keeps its own initial load.
 */
export function useVisiblePolling(
  callback: () => void,
  intervalMs: number,
  options?: VisiblePollingOptions
): void {
  const enabled = options?.enabled ?? true;
  const isVisible = usePageVisibility();

  // The callback lives in a ref updated on every render and stays OUT of the effect's
  // deps, so a caller whose closure changes identity every render does not restart the
  // cadence -- while the interval still runs the freshest closure.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // The mount counts as a run for the floor: a tab opened in the background does its
  // initial load, and looking at it seconds later does not immediately repeat it.
  const lastRunRef = useRef<number>(Date.now());
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    const wasFirstRun = isFirstRunRef.current;
    isFirstRunRef.current = false;

    if (!isVisible || !enabled) {
      return;
    }

    const run = () => {
      lastRunRef.current = Date.now();
      callbackRef.current();
    };

    // One refresh on becoming active again -- never the ticks that piled up while
    // hidden, and never on the initial mount.
    if (!wasFirstRun && Date.now() - lastRunRef.current >= REFRESH_FLOOR_MS) {
      run();
    }

    const interval = setInterval(run, intervalMs);
    return () => clearInterval(interval);
  }, [isVisible, enabled, intervalMs]);
}
